<script lang="ts">
	// The design system's MapPin (design/components/surfaces/MapPin.jsx): one
	// place on an OpenStreetMap map, and nothing else. The owner puts a shop
	// where it is (UC-33); diners never see this, the catalogue links out to
	// Google Maps instead.
	//
	// Every way to move the pin works by tap. A tap puts the pin there;
	// holding and moving drags it, the same tap continued. Zoom keeps the pin
	// in the middle, so the map never pans under a finger: a pan and a drag of
	// the pin would be one gesture with two meanings. The arrow keys move the
	// pin while the map has focus, and Enter places one in the middle. A place
	// typed beside the map that falls outside the view brings the view to it.
	//
	// The map is physical, so its surface is left to right in both languages:
	// an Arabic page must not mirror the world. Values come out in degrees
	// rounded to six places, exactly what a place in millionths holds. The
	// credit is OpenStreetMap's licence condition: fixed English, always
	// shown, a real link.

	import { untrack } from 'svelte';
	import { pressable } from './pressable';

	interface Place {
		lat: number;
		lng: number;
	}

	interface Props {
		testId: string;
		lat: number | null;
		lng: number | null;
		onChange: (place: Place) => void;
		/** Where the map looks while there is no pin. */
		center?: Place;
		zoom?: number;
		emptyZoom?: number;
		minZoom?: number;
		maxZoom?: number;
		label: string;
		/** Said over the map while there is no pin. */
		emptyHint?: string;
		zoomInLabel: string;
		zoomOutLabel: string;
		disabled?: boolean;
	}

	let {
		testId,
		lat,
		lng,
		onChange,
		center = { lat: 30.0444, lng: 31.2357 },
		zoom = 16,
		emptyZoom = 12,
		minZoom = 3,
		maxZoom = 19,
		label,
		emptyHint,
		zoomInLabel,
		zoomOutLabel,
		disabled = false,
	}: Props = $props();

	const TILE = 256;

	function clampLat(v: number): number {
		return Math.max(-85.0511, Math.min(85.0511, v));
	}

	function project(la: number, ln: number, z: number) {
		const s = TILE * 2 ** z;
		const r = (clampLat(la) * Math.PI) / 180;
		return {
			x: ((ln + 180) / 360) * s,
			y: ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * s,
		};
	}

	function unproject(x: number, y: number, z: number): Place {
		const s = TILE * 2 ** z;
		const n = Math.PI - (2 * Math.PI * y) / s;
		return {
			lat: (180 / Math.PI) * Math.atan(Math.sinh(n)),
			lng: (((x / s) * 360 - 180 + 540) % 360) - 180,
		};
	}

	function r6(v: number): number {
		return Math.round(v * 1e6) / 1e6;
	}

	const has = $derived(lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng));

	let surface = $state<HTMLDivElement>();
	let box = $state({ w: 0, h: 0 });
	let dragging = false;
	// Where the map looks and how close, set once from the first place and
	// then moved only by zoom or by a typed place outside the view.
	let z = $state(untrack(() => (has ? zoom : emptyZoom)));
	let view = $state<Place>(untrack(() => (has ? { lat: lat!, lng: lng! } : center)));

	$effect(() => {
		const el = surface;
		if (!el) return;
		const read = () => (box = { w: el.clientWidth, h: el.clientHeight });
		read();
		const observer = new ResizeObserver(read);
		observer.observe(el);
		return () => observer.disconnect();
	});

	const c = $derived(project(view.lat, view.lng, z));
	const x0 = $derived(c.x - box.w / 2);
	const y0 = $derived(c.y - box.h / 2);

	// A place set from outside that falls outside the view brings the view to
	// it; one already visible leaves the map still, so a tap never makes the
	// ground jump.
	$effect(() => {
		if (!has || !box.w) return;
		const la = lat!;
		const ln = lng!;
		const { w, h } = box;
		untrack(() => {
			const p = project(la, ln, z);
			if (Math.abs(p.x - c.x) > w / 2 - 24 || Math.abs(p.y - c.y) > h / 2 - 24) {
				view = { lat: la, lng: ln };
			}
		});
	});

	const tiles = $derived.by(() => {
		const out: { key: string; src: string; x: number; y: number }[] = [];
		if (!box.w) return out;
		const n = 2 ** z;
		for (let tx = Math.floor(x0 / TILE); tx <= Math.floor((x0 + box.w) / TILE); tx++) {
			for (let ty = Math.floor(y0 / TILE); ty <= Math.floor((y0 + box.h) / TILE); ty++) {
				if (ty < 0 || ty >= n) continue;
				const wx = ((tx % n) + n) % n;
				out.push({
					key: `${z}/${tx}/${ty}`,
					src: `https://tile.openstreetmap.org/${z}/${wx}/${ty}.png`,
					x: tx * TILE - x0,
					y: ty * TILE - y0,
				});
			}
		}
		return out;
	});

	const pin = $derived(has && box.w ? project(lat!, lng!, z) : null);

	function at(px: number, py: number) {
		const place = unproject(x0 + px, y0 + py, z);
		onChange({ lat: r6(place.lat), lng: r6(place.lng) });
	}

	function fromEvent(event: PointerEvent) {
		const r = surface!.getBoundingClientRect();
		at(event.clientX - r.left, event.clientY - r.top);
	}

	function down(event: PointerEvent) {
		if (disabled) return;
		dragging = true;
		try {
			surface!.setPointerCapture(event.pointerId);
		} catch {
			// A synthetic pointer cannot be captured; the tap still lands.
		}
		fromEvent(event);
	}

	function move(event: PointerEvent) {
		if (dragging) fromEvent(event);
	}

	function up() {
		dragging = false;
	}

	function zoomBy(d: number) {
		const next = Math.max(minZoom, Math.min(maxZoom, z + d));
		if (next === z) return;
		if (has) view = { lat: lat!, lng: lng! };
		z = next;
	}

	function key(event: KeyboardEvent) {
		if (disabled) return;
		const step = event.shiftKey ? 40 : 8;
		const arrows: Record<string, [number, number]> = {
			ArrowLeft: [-step, 0],
			ArrowRight: [step, 0],
			ArrowUp: [0, -step],
			ArrowDown: [0, step],
		};
		const d = arrows[event.key];
		if (d && has) {
			event.preventDefault();
			const p = project(lat!, lng!, z);
			at(p.x - x0 + d[0], p.y - y0 + d[1]);
		} else if ((event.key === 'Enter' || event.key === ' ') && !has) {
			event.preventDefault();
			at(box.w / 2, box.h / 2);
		} else if (event.key === '+' || event.key === '=') {
			zoomBy(1);
		} else if (event.key === '-') {
			zoomBy(-1);
		}
	}
</script>

<div class="map-pin" data-testid={testId}>
	<!-- The design's map is role="application": it takes the keys itself. -->
	<!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
	<div
		bind:this={surface}
		class="surface"
		class:disabled
		dir="ltr"
		role="application"
		aria-label={label}
		tabindex={disabled ? -1 : 0}
		aria-disabled={disabled || undefined}
		data-testid="{testId}-surface"
		onpointerdown={down}
		onpointermove={move}
		onpointerup={up}
		onpointercancel={up}
		onkeydown={key}
	>
		{#each tiles as tile (tile.key)}
			<img
				class="tile"
				src={tile.src}
				alt=""
				draggable="false"
				width={TILE}
				height={TILE}
				onerror={(event) => ((event.currentTarget as HTMLImageElement).style.visibility = 'hidden')}
				style:transform="translate({tile.x}px, {tile.y}px)"
			/>
		{/each}
		{#if pin}
			<!-- A teardrop: a square with one sharp corner, turned to point
			     down. Its tip lands on the place. -->
			<span
				class="pin"
				aria-hidden="true"
				data-testid="{testId}-pin"
				style:transform="translate(calc({pin.x - x0}px - var(--pin-size) / 2), calc({pin.y - y0}px -
				var(--pin-size) * 1.207)) rotate(-45deg)"
			></span>
		{/if}
	</div>

	{#if !has && emptyHint}
		<span class="hint">{emptyHint}</span>
	{/if}

	<div class="zoom">
		<button
			type="button"
			class="ctl"
			data-press
			data-testid="{testId}-zoom-in"
			{@attach pressable}
			onclick={() => zoomBy(1)}
			disabled={disabled || z >= maxZoom}
			aria-label={zoomInLabel}
			title={zoomInLabel}
		>
			<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
				<path d="M12 5v14M5 12h14" />
			</svg>
		</button>
		<button
			type="button"
			class="ctl"
			data-press
			data-testid="{testId}-zoom-out"
			{@attach pressable}
			onclick={() => zoomBy(-1)}
			disabled={disabled || z <= minZoom}
			aria-label={zoomOutLabel}
			title={zoomOutLabel}
		>
			<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
				<path d="M5 12h14" />
			</svg>
		</button>
	</div>

	<a
		class="credit"
		dir="ltr"
		href="https://www.openstreetmap.org/copyright"
		target="_blank"
		rel="noopener"
		data-testid="{testId}-credit">© OpenStreetMap contributors</a
	>
</div>

<style>
	.map-pin {
		position: relative;
	}

	.surface {
		position: relative;
		block-size: var(--map-h);
		overflow: hidden;
		background: var(--surface-well);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
		cursor: crosshair;
		touch-action: none;
		user-select: none;
	}

	.surface.disabled {
		cursor: default;
	}

	/* Tiles stay light in the dark theme: they are the map, not a surface. */
	.tile {
		position: absolute;
		inset-block-start: 0;
		inset-inline-start: 0;
		inline-size: 256px;
		block-size: 256px;
		max-inline-size: none;
		pointer-events: none;
	}

	/* The fill and ring are the --speed and --on-speed pair, the same in
	   both themes. */
	.pin {
		position: absolute;
		inset-block-start: 0;
		inset-inline-start: 0;
		inline-size: var(--pin-size);
		block-size: var(--pin-size);
		box-sizing: border-box;
		background: var(--speed);
		border: 3px solid var(--on-speed);
		border-radius: 50% 50% 50% 0;
		pointer-events: none;
	}

	.hint {
		position: absolute;
		inset-block-start: 50%;
		inset-inline-start: 50%;
		transform: translate(-50%, -50%);
		max-inline-size: calc(100% - 2 * var(--space-9));
		padding: var(--space-3) var(--space-5);
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-input);
		font-size: var(--text-2xs);
		font-weight: 600;
		text-align: center;
		pointer-events: none;
	}

	/* inset-inline-start is the right edge on an Arabic page, so the box
	   moves the other way to sit centred. */
	:global([dir='rtl']) .hint {
		transform: translate(50%, -50%);
	}

	.zoom {
		position: absolute;
		inset-block-start: var(--space-3);
		inset-inline-end: var(--space-3);
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.ctl {
		inline-size: var(--ctl-min);
		block-size: var(--ctl-min);
		display: grid;
		place-items: center;
		padding: 0;
		background: var(--surface-card);
		color: var(--text-strong);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-input);
		cursor: pointer;
	}

	.ctl svg {
		fill: none;
		stroke: currentColor;
		stroke-width: 1.8;
		stroke-linecap: round;
	}

	.credit {
		position: absolute;
		inset-block-end: var(--space-2);
		inset-inline-end: var(--space-2);
		padding: 2px var(--space-3);
		background: var(--surface-card);
		color: var(--text-body);
		border-radius: var(--radius-chip);
		font-family: var(--font-sans);
		font-size: 11px;
		line-height: 1.6;
		text-decoration: none;
	}
</style>
